import { ReactNode, useRef, useState } from "react";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

type ConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  children: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  showCancelButton?: boolean;
  draggable?: boolean;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "full";
};

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  children,
  confirmText,
  cancelText,
  confirmDisabled = false,
  showCancelButton = true,
  draggable = false,
  size = "md",
}: ConfirmationModalProps) => {
  const { t } = useTranslation();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragState = useRef<{ startX: number; startY: number; ox: number; oy: number; baseLeft: number; baseTop: number; w: number; h: number } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent): void => {
    if (!draggable) return;
    e.preventDefault();
    const el = contentRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      ox: offset.x,
      oy: offset.y,
      baseLeft: rect.left - offset.x,
      baseTop: rect.top - offset.y,
      w: rect.width,
      h: rect.height,
    };
    const handleMouseMove = (ev: MouseEvent): void => {
      const ds = dragState.current;
      if (!ds) return;
      const rawX = ds.ox + ev.clientX - ds.startX;
      const rawY = ds.oy + ev.clientY - ds.startY;
      const minX = -ds.baseLeft;
      const maxX = window.innerWidth - ds.baseLeft - ds.w;
      const minY = -ds.baseTop;
      const maxY = window.innerHeight - ds.baseTop - ds.h;
      setOffset({
        x: Math.max(minX, Math.min(maxX, rawX)),
        y: Math.max(minY, Math.min(maxY, rawY)),
      });
    };
    const handleMouseUp = (): void => {
      dragState.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleClose = (): void => {
    setOffset({ x: 0, y: 0 });
    onClose();
  };

  const content = (
    <ModalContent ref={contentRef}>
      <ModalHeader
        onMouseDown={handleMouseDown}
        cursor={draggable ? "grab" : undefined}
        userSelect={draggable ? "none" : undefined}
      >
        {title}
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody>{children}</ModalBody>
      <ModalFooter gap={2}>
        {showCancelButton && (
          <Button variant="outline" onClick={handleClose}>
            {cancelText || t("cancel")}
          </Button>
        )}
        <Button colorScheme="teal" onClick={onConfirm} isDisabled={confirmDisabled}>
          {confirmText || t("confirm")}
        </Button>
      </ModalFooter>
    </ModalContent>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={size}>
      <ModalOverlay />
      {draggable ? (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          pointerEvents="none"
          zIndex="modal"
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
          sx={{ "& > *": { pointerEvents: "auto" } }}
        >
          {content}
        </Box>
      ) : (
        content
      )}
    </Modal>
  );
};

export default ConfirmationModal;
